import React, { useCallback, useEffect, useState } from 'react';
import { FiDownload, FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import {
  billingAPI, budgetForecastsAPI, creditNotesAPI, downloadBlob, paymentPlansAPI, quotationsAPI, serviceAgreementsAPI,
  signingAPI, statementsAPI, studentsAPI, usersAPI,
} from '../services/api';
import { useOpenParam } from '../utils/openParam';
import { useAuth } from '../contexts/AuthContext';
import './Billing.css';

const CATEGORIES = [
  'ADMISSION', 'CONSULTATION', 'TRANSLATION', 'INSURANCE', 'IKAMET', 'HOUSING', 'OTHER',
];
const METHODS = ['CASH', 'BANK_TRANSFER', 'CARD', 'ONLINE', 'OTHER'];

const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '—');

const emptyPriceItem = {
  name: '', category: 'OTHER', unit: '', default_unit_price: '0.00',
  currency: 'USD', tax_rate_pct: '0.00', description: '', is_active: true, sort_order: 0,
};

function PriceBook() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(emptyPriceItem);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingAPI.priceItems.list();
      setItems(res.data?.results ?? res.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setEditing(item ? item.id : 'new');
    setForm(item ? { ...item } : { ...emptyPriceItem });
    setError('');
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editing === 'new') await billingAPI.priceItems.create(form);
      else await billingAPI.priceItems.update(editing, form);
      setEditing(null);
      load();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Save failed.');
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);
  const remove = async (id) => {
    await billingAPI.priceItems.delete(id);
    setConfirmDelete(null);
    load();
  };

  return (
    <div className="billing-panel">
      <div className="billing-panel-head">
        <h3>Service price book</h3>
        <button className="btn-primary btn-sm" onClick={() => startEdit(null)}>
          <FiPlus size={13} /> Add service
        </button>
      </div>
      <p className="billing-hint">
        The services and prices staff pick from when building a quotation or receipt.
        A line stays editable after it is added from here.
      </p>

      {editing && (
        <form className="billing-form" onSubmit={save}>
          <div className="billing-form-grid">
            <label>Name
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </label>
            <label>Category
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Unit
              <input placeholder="per student / per page…" value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </label>
            <label>Default unit price
              <input type="number" step="0.01" value={form.default_unit_price}
                onChange={(e) => setForm({ ...form, default_unit_price: e.target.value })} />
            </label>
            <label>Currency
              <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </label>
            <label>Tax rate %
              <input type="number" step="0.01" value={form.tax_rate_pct}
                onChange={(e) => setForm({ ...form, tax_rate_pct: e.target.value })} />
            </label>
            <label>Sort order
              <input type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            </label>
            <label className="billing-check">
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
            </label>
          </div>
          <label className="billing-full">Description (optional, shown under the line)
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          {error && <div className="billing-error">{error}</div>}
          <div className="billing-form-actions">
            <button type="submit" className="btn-primary btn-sm">Save</button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? <div className="billing-loading">Loading…</div> : (
        <table className="billing-table">
          <thead>
            <tr><th>Name</th><th>Category</th><th>Unit</th><th className="num">Price</th><th className="num">Tax %</th><th>Active</th><th /></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="billing-empty">No services yet.</td></tr>}
            {items.map((it) => (
              <tr key={it.id}>
                <td>{it.name}{it.description && <span className="billing-sub">{it.description}</span>}</td>
                <td>{it.category_display || it.category}</td>
                <td>{it.unit || '—'}</td>
                <td className="num">{it.currency} {Number(it.default_unit_price).toFixed(2)}</td>
                <td className="num">{Number(it.tax_rate_pct).toFixed(2)}</td>
                <td>{it.is_active ? 'Yes' : 'No'}</td>
                <td className="billing-row-actions">
                  <button onClick={() => startEdit(it)} title="Edit"><FiEdit2 size={13} /></button>
                  {confirmDelete === it.id ? (
                    <>
                      <button onClick={() => remove(it.id)} title="Confirm delete" style={{ color: '#b91c1c' }}>Confirm</button>
                      <button onClick={() => setConfirmDelete(null)} title="Cancel">Cancel</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDelete(it.id)} title="Delete"><FiTrash2 size={13} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const TEMPLATE_TEXT_FIELDS = [
  ['company_name', 'Registered company name (shown in the FROM block, footer and stamp)', 'input'],
  ['company_address', 'Company address', 'textarea'],
  ['company_email', 'Email', 'input'],
  ['company_phone', 'Tel', 'input'],
  ['company_whatsapp', 'WhatsApp', 'input'],
  ['website', 'Website', 'input'],
  ['footer_tagline', 'Footer tagline', 'input'],
  ['social_tiktok', 'TikTok link (logo in the footer and client emails)', 'input'],
  ['social_instagram', 'Instagram link', 'input'],
  ['social_facebook', 'Facebook link', 'input'],
  ['social_handle', 'Handle printed beside the logos', 'input'],
  ['accent_color', 'Accent colour (hex)', 'input'],
  ['default_terms', 'Notes & terms  ·  "- " lines = bullets  ·  overridden per document', 'textarea'],
  ['section_b_disclaimer', 'Section B disclaimer (above the third-party costs table)', 'textarea'],
  ['signature_label', 'Signature line label', 'input'],
  ['thank_you_line', 'Closing thank-you line', 'input'],
  ['quotation_intro', 'Quotation intro paragraph', 'textarea'],
  ['invoice_intro', 'Invoice intro paragraph', 'textarea'],
  ['receipt_intro', 'Receipt intro paragraph', 'textarea'],
];

// Only the methods filled in here ever print on a document — an empty method
// is left off entirely, never shown as a blank placeholder.
const PAYMENT_FIELDS = [
  ['pay_cash', 'Cash — where / how to pay', 'textarea'],
  ['pay_bank_name', 'Bank name', 'input'],
  ['pay_bank_account_name', 'Account name', 'input'],
  ['pay_bank_account_number', 'Account number', 'input'],
  ['pay_bank_branch', 'Branch', 'input'],
  ['pay_bank_swift', 'SWIFT / BIC', 'input'],
  ['pay_mobile_money', 'Mobile money (EcoCash / InnBucks…)', 'textarea'],
  ['pay_other', 'Other method', 'textarea'],
];

function TemplateEditor() {
  const [tmpl, setTmpl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    billingAPI.template.get().then((r) => setTmpl(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const fd = new FormData();
      TEMPLATE_TEXT_FIELDS.forEach(([k]) => fd.append(k, tmpl[k] ?? ''));
      PAYMENT_FIELDS.forEach(([k]) => fd.append(k, tmpl[k] ?? ''));
      if (logoFile) fd.append('logo', logoFile);
      const r = await billingAPI.template.update(fd);
      setTmpl(r.data); setLogoFile(null);
      setMsg('Saved. New documents use these details; the layout and typography are fixed.');
    } catch (err) {
      setMsg(err.response?.data ? JSON.stringify(err.response.data) : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!tmpl) return <div className="billing-loading">Loading…</div>;

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Document template</h3></div>
      <p className="billing-hint">
        Editable text on every quotation, invoice and receipt. The logo, the layout,
        and the way the text is arranged stay the same.
      </p>
      <form className="billing-form" onSubmit={save}>
        <label className="billing-full">Logo (PNG/JPG — leave empty to keep the current one / the wordmark)
          <input type="file" accept="image/png,image/jpeg,image/webp"
            onChange={(e) => setLogoFile(e.target.files[0] || null)} />
          {tmpl.logo && <a className="billing-sub" href={tmpl.logo} target="_blank" rel="noreferrer">current logo</a>}
        </label>
        {TEMPLATE_TEXT_FIELDS.map(([k, label, kind]) => (
          <label key={k} className="billing-full">{label}
            {kind === 'textarea'
              ? <textarea rows={3} value={tmpl[k] ?? ''} onChange={(e) => setTmpl({ ...tmpl, [k]: e.target.value })} />
              : <input value={tmpl[k] ?? ''} onChange={(e) => setTmpl({ ...tmpl, [k]: e.target.value })} />}
          </label>
        ))}
        <h4 style={{ margin: '18px 0 4px' }}>Payment methods</h4>
        <p className="billing-hint" style={{ margin: '0 0 10px' }}>
          Only a filled-in method ever prints on a document — leave a field empty to keep it off entirely.
        </p>
        {PAYMENT_FIELDS.map(([k, label, kind]) => (
          <label key={k} className="billing-full">{label}
            {kind === 'textarea'
              ? <textarea rows={2} value={tmpl[k] ?? ''} onChange={(e) => setTmpl({ ...tmpl, [k]: e.target.value })} />
              : <input value={tmpl[k] ?? ''} onChange={(e) => setTmpl({ ...tmpl, [k]: e.target.value })} />}
          </label>
        ))}
        {msg && <div className="billing-note">{msg}</div>}
        <div className="billing-form-actions">
          <button type="submit" className="btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save template'}</button>
        </div>
      </form>
    </div>
  );
}

// Founder rule, 19 Oct 2026 (mirrors deals/billing.py): every internal staff
// member drafts; COO/CEO/CSM/Chairman/CFO (+ Admin) issue and send; only the
// CFO records payments.
const ISSUER_NOTE = 'The COO, CEO, CSM, Chairman, CFO or Admin issues this.';
const ISSUER_NOTE_STYLE = { fontSize: 12, color: '#64748b' };

const emptyReceiptLine = () => ({ description: '', amount: '' });
const emptyReceipt = () => ({
  student: '', currency: 'USD', payment_method: 'BANK_TRANSFER',
  payment_reference: '', received_on: '', notes: '', lines: [emptyReceiptLine()],
});
const receiptLinesTotal = (lines) => lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

// The CFO has no student list (finance-only access), so receipts pick the
// client through the Payments desk's identity-only search instead.
function ClientSearchSelect({ value, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      billingAPI.paymentDesk.clients(q.trim())
        .then((r) => setResults((r.data?.results || []).filter((c) => c.kind === 'student')))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <>
      <input placeholder="Search name or ID…" value={q} onChange={(e) => setQ(e.target.value)} />
      <select value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">{q.trim().length < 2 ? 'Type 2+ letters above' : `${results.length} found`}</option>
        {results.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.ref})</option>)}
      </select>
    </>
  );
}

// ── Invoice ↔ receipt links (founder, 24 Oct 2026 — brainstorms/2026-10-24-invoice-receipt-linking.md) ──

const errMsg = (err, fallback) => {
  const d = err?.response?.data;
  if (!d) return fallback;
  if (Array.isArray(d)) return d.join(' ');
  return d.detail || d.error || Object.values(d).flat().join(' ') || fallback;
};
const money = (cur, v) => `${cur} ${Number(v || 0).toFixed(2)}`;

const PDF_BY_KIND = {
  quotation: quotationsAPI.pdf, invoice: quotationsAPI.pdf, receipt: billingAPI.receipts.pdf,
  credit_note: creditNotesAPI.pdf, payment_plan: paymentPlansAPI.pdf,
  budget_forecast: budgetForecastsAPI.pdf, service_agreement: serviceAgreementsAPI.pdf,
};

// A document number that downloads that document's PDF.
function DocLink({ kind, id, number }) {
  const open = async () => {
    const res = await PDF_BY_KIND[kind](id);
    downloadBlob(res, `${number || kind}.pdf`);
  };
  return <button type="button" className="billing-link" onClick={open} title="Download PDF">{number || 'DRAFT'}</button>;
}

// Any document row can jump to its client's billing file.
const ClientFileContext = React.createContext(() => {});
function ClientFileButton({ student }) {
  const open = React.useContext(ClientFileContext);
  if (!student) return null;
  return <button type="button" onClick={() => open(student)} title="Every billing document for this client">Client file</button>;
}

const PAY_PILL = {
  PAID: ['Paid', 'billing-pill-green'], PART_PAID: ['Part-paid', 'billing-pill-amber'], UNPAID: ['Unpaid', ''],
  OVERDUE: ['Overdue', 'billing-pill-red'], CREDITED: ['Credited', ''], VOID: ['Void', 'billing-pill-red'],
};
function PayPill({ status }) {
  const [label, cls] = PAY_PILL[status] || [];
  return label ? <span className={`billing-pill ${cls}`}>{label}</span> : null;
}

// "Payments received" under an invoice: each receipt that paid it, clickable.
function PaymentsReceived({ invoice }) {
  if (!invoice.payments?.length) return null;
  return (
    <>
      Payments received:{' '}
      {invoice.payments.map((p, i) => (
        <span key={p.receipt}>
          {i > 0 && ' · '}
          <DocLink kind="receipt" id={p.receipt} number={p.receipt_number} /> {fmtDate(p.received_on)}, {p.payment_method},{' '}
          {money(invoice.currency, p.amount)}{p.amount !== p.receipt_amount && ` (of ${Number(p.receipt_amount).toFixed(2)})`}
        </span>
      ))}
    </>
  );
}

// Link an issued receipt to invoice(s), split it, mark the rest "not for an
// invoice", or take it off an invoice (with a reason). CFO / Chairman only.
function MatchEditor({ receipt, suggested, onSaved, onCancel }) {
  const [invoices, setInvoices] = useState(null);
  const existing = Object.fromEntries((receipt.allocations || []).map((a) => [String(a.invoice), a.amount]));
  const [parts, setParts] = useState(() => {
    const init = { ...existing };
    (suggested || []).forEach((s) => {
      init[String(s.invoice)] = (Number(init[String(s.invoice)] || 0) + Number(s.amount)).toFixed(2);
    });
    return init;
  });
  const [reason, setReason] = useState(receipt.not_for_invoice_reason || '');
  const [unlinkReason, setUnlinkReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    billingAPI.receipts.openInvoices(receipt.student, receipt.currency)
      .then((r) => setInvoices(r.data?.results || []))
      .catch(() => setInvoices([]));
  }, [receipt.student, receipt.currency]);

  if (invoices === null) return <div className="billing-loading">Loading the client's invoices…</div>;
  // Invoices this receipt is already on stay listed even once they're fully paid.
  const rows = [...invoices];
  (receipt.allocations || []).forEach((a) => {
    if (!rows.some((inv) => String(inv.id) === String(a.invoice))) {
      rows.push({ id: a.invoice, number: a.invoice_number, currency: receipt.currency, balance: '0.00', subject: '' });
    }
  });
  const applied = Object.values(parts).reduce((s, v) => s + (Number(v) || 0), 0);
  const rest = Number(receipt.amount) - applied;
  const reduced = Object.entries(existing).some(([id, amt]) => (Number(parts[id]) || 0) < Number(amt));

  const save = async () => {
    setBusy(true); setError('');
    try {
      await billingAPI.receipts.match(receipt.id, {
        allocations: Object.entries(parts).filter(([, v]) => Number(v) > 0).map(([invoice, amount]) => ({ invoice, amount })),
        not_for_invoice_reason: rest > 0.004 ? reason : '',
        unlink_reason: reduced ? unlinkReason : '',
      });
      onSaved();
    } catch (err) {
      setError(errMsg(err, 'Could not save the link.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="billing-match">
      {rows.length === 0 && <div className="billing-hint" style={{ margin: 0 }}>This client has no open {receipt.currency} invoice.</div>}
      {rows.map((inv) => {
        const mine = Number(existing[String(inv.id)] || 0);
        return (
          <div className="billing-match-row" key={inv.id}>
            <input type="number" step="0.01" min="0" placeholder="0.00" value={parts[String(inv.id)] ?? ''}
              onChange={(e) => setParts((p) => ({ ...p, [String(inv.id)]: e.target.value }))} />
            <span>
              → <strong>{inv.number}</strong> — {money(inv.currency, Number(inv.balance) + mine)} open
              {inv.subject ? <span className="billing-sub" style={{ display: 'inline', marginLeft: 6 }}>{inv.subject}</span> : null}
            </span>
          </div>
        );
      })}
      <div className="billing-match-row">
        Applied {money(receipt.currency, applied)} of {money(receipt.currency, receipt.amount)}
        {rest < -0.004 && <span className="billing-pill billing-pill-red">More than the receipt</span>}
      </div>
      {rest > 0.004 && (
        <div className="billing-match-row">
          <span>Rest ({money(receipt.currency, rest)}) is not for an invoice — why?</span>
          <input className="billing-wide" placeholder="e.g. Advance / credit on account" value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </div>
      )}
      {reduced && (
        <div className="billing-match-row">
          <span>Taking money off an invoice — why?</span>
          <input className="billing-wide" placeholder="e.g. Recorded against the wrong invoice" value={unlinkReason}
            onChange={(e) => setUnlinkReason(e.target.value)} />
        </div>
      )}
      {error && <div className="billing-error">{error}</div>}
      <div className="billing-row-actions" style={{ textAlign: 'left', marginTop: 6 }}>
        <button type="button" onClick={save} disabled={busy || rest < -0.004 || (rest > 0.004 && !reason.trim())
          || (reduced && !unlinkReason.trim())}>
          {busy ? 'Saving…' : 'Confirm'}
        </button>
        {onCancel && <button type="button" onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

// Outstanding → Match receipts: issued receipts on no invoice, best match pre-filled.
function MatchReceipts({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    billingAPI.receipts.unmatched()
      .then((r) => setRows(r.data?.results || []))
      .catch((err) => setError(errMsg(err, 'Could not load the receipts.')));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error) return <div className="billing-error">{error}</div>;
  if (!rows) return <div className="billing-loading">Loading…</div>;
  return (
    <div style={{ marginBottom: 20 }}>
      <h4>Match receipts</h4>
      <p className="billing-hint">
        These receipts were issued without saying which invoice they pay, so the invoices still look unpaid. Check the
        suggested match, change it if needed, and confirm. Nothing is linked until you confirm.
      </p>
      {rows.length === 0 && <div className="billing-empty">Every receipt is matched.</div>}
      {rows.map((r) => (
        <div key={r.id}>
          <div style={{ fontSize: 13, margin: '8px 0 4px' }}>
            <DocLink kind="receipt" id={r.id} number={r.receipt_number} /> — <strong>{r.student_name}</strong>,{' '}
            {money(r.currency, r.amount)} received {fmtDate(r.received_on)}
            {Number(r.unallocated) !== Number(r.amount) && <> · {money(r.currency, r.unallocated)} not yet applied</>}
            {(r.lines || []).length > 0 && <span className="billing-sub">{r.lines.map((l) => l.description).join('; ')}</span>}
          </div>
          <MatchEditor receipt={r} suggested={r.suggested} onSaved={() => { load(); onChanged?.(); }} />
        </div>
      ))}
    </div>
  );
}

// A new deadline for what's left on an invoice — CFO or Chairman, with a reason.
function NewDeadline({ invoiceId, onSaved, onCancel }) {
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try {
      await quotationsAPI.newDeadline(invoiceId, { due_date: date, reason });
      onSaved();
    } catch (err) {
      setError(errMsg(err, 'Could not set the deadline.'));
    } finally { setBusy(false); }
  };
  return (
    <div className="billing-match-row">
      New deadline for the balance:
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <input className="billing-wide" placeholder="Why? e.g. Client pays the rest after the visa" value={reason}
        onChange={(e) => setReason(e.target.value)} />
      <span className="billing-row-actions">
        <button type="button" onClick={save} disabled={busy || !date || !reason.trim()}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </span>
      {error && <div className="billing-error" style={{ width: '100%' }}>{error}</div>}
    </div>
  );
}

function DeadlineHistory({ changes }) {
  if (!changes?.length) return null;
  const c = changes[0];
  return (
    <span title={changes.map((x) => `${fmtDate(x.old_due_date)} → ${fmtDate(x.new_due_date)}: ${x.reason} (${x.changed_by})`).join('\n')}>
      Deadline moved to {fmtDate(c.new_due_date)} (was {fmtDate(c.old_due_date)}) — {c.reason}
      {changes.length > 1 && ` · ${changes.length} changes`}
    </span>
  );
}

// Billing → Client billing file: one client's documents, chained.
function ClientFile({ initialStudent }) {
  const [studentId, setStudentId] = useState(initialStudent ? String(initialStudent) : '');
  const [students, setStudents] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [matching, setMatching] = useState(null);

  useEffect(() => {
    studentsAPI.list({ page_size: 500 }).then((r) => setStudents(r.data?.results ?? r.data ?? [])).catch(() => {});
  }, []);
  useEffect(() => { if (initialStudent) setStudentId(String(initialStudent)); }, [initialStudent]);
  const load = useCallback(() => {
    if (!studentId) { setData(null); return; }
    setError('');
    billingAPI.clientFile(studentId).then((r) => setData(r.data)).catch((err) => setError(errMsg(err, 'Could not load this client.')));
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const statement = async () => downloadBlob(await statementsAPI.pdf(studentId), `statement-${studentId}.pdf`);

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Client billing file</h3></div>
      <p className="billing-hint">
        Every billing document for one client, connected: quotation → invoice → the receipts, credit notes and payment
        plan on it. Loose ends are in amber. Click a number to download that document.
      </p>
      <div className="billing-form" style={{ marginBottom: 14 }}>
        <div className="billing-form-grid">
          <label>Client
            {students.length ? (
              <select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <option value="">Select…</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.first_name} ${s.last_name}`}</option>)}
              </select>
            ) : <ClientSearchSelect value={studentId} onChange={setStudentId} />}
          </label>
        </div>
      </div>
      {error && <div className="billing-error">{error}</div>}
      {data && (
        <>
          <div className="billing-summary">
            <div><strong>{data.student.name}</strong> {data.student.ref}</div>
            {data.totals.length === 0 && <div>No issued invoices yet</div>}
            {data.totals.map((t) => (
              <div key={t.currency}>
                Invoiced {money(t.currency, t.invoiced)} · Paid {money(t.currency, t.paid)}
                {Number(t.credited) > 0 && <> · Credited {money(t.currency, t.credited)}</>}
                {' · '}<strong style={{ fontSize: 14 }}>Outstanding {money(t.currency, t.outstanding)}</strong>
              </div>
            ))}
            <div className="billing-row-actions" style={{ background: 'none', border: 'none', padding: 0 }}>
              <button type="button" onClick={statement}><FiDownload size={13} /> Statement</button>
            </div>
          </div>

          {(data.unmatched_receipts.length > 0 || data.ledger_without_receipt.length > 0) && (
            <div className="billing-chain billing-loose">
              <div className="billing-chain-head">Loose ends</div>
              <ul>
                {data.unmatched_receipts.map((r) => (
                  <li key={`r${r.id}`}>
                    Receipt <DocLink kind="receipt" id={r.id} number={r.number} /> ({money(r.currency, r.amount)},{' '}
                    {fmtDate(r.received_on)}) isn't linked to an invoice — {money(r.currency, r.unallocated)} not applied.{' '}
                    {matching === r.id ? null : (
                      <button type="button" className="billing-link" onClick={() => setMatching(r.id)}>Match it</button>
                    )}
                    {matching === r.id && (
                      <MatchReceiptLoader id={r.id} onSaved={() => { setMatching(null); load(); }} onCancel={() => setMatching(null)} />
                    )}
                  </li>
                ))}
                {data.ledger_without_receipt.map((e) => (
                  <li key={`l${e.id}`}>
                    {e.description} — {money(e.currency, e.amount)} marked Received on the payments page
                    {e.reviewed_at ? ` (${fmtDate(e.reviewed_at)})` : ''} with no billing receipt.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.quotations.filter((q) => !q.invoices.length).map((q) => (
            <div className="billing-chain" key={`q${q.id}`}>
              <div className="billing-chain-head">
                Quotation <DocLink kind="quotation" id={q.id} number={q.number} />
                <span className="billing-pill">{q.status_display}</span>
                <span style={{ fontWeight: 400 }}>{money(q.currency, q.amount)} · issued {fmtDate(q.issue_date)}</span>
              </div>
            </div>
          ))}

          {data.invoices.map((inv) => (
            <div className={`billing-chain ${inv.status === 'ISSUED' && Number(inv.balance) > 0 ? 'billing-loose' : ''}`} key={`i${inv.id}`}>
              <div className="billing-chain-head">
                {inv.source_quotation && <>Quotation <DocLink kind="quotation" id={inv.source_quotation.id} number={inv.source_quotation.number} /> →</>}
                Invoice <DocLink kind="invoice" id={inv.id} number={inv.number} />
                {inv.status === 'ISSUED' ? <PayPill status={inv.payment_status} /> : <span className="billing-pill">{inv.status_display}</span>}
                <span style={{ fontWeight: 400 }}>
                  {money(inv.currency, inv.amount)} · paid {Number(inv.paid).toFixed(2)} · balance {Number(inv.balance).toFixed(2)}
                  {inv.due_date && ` · due ${fmtDate(inv.due_date)}`}
                </span>
              </div>
              <ul>
                {inv.payments.map((p) => (
                  <li key={`p${p.receipt}`}>
                    Receipt <DocLink kind="receipt" id={p.receipt} number={p.receipt_number} /> — {money(inv.currency, p.amount)},{' '}
                    {fmtDate(p.received_on)}, {p.payment_method}
                    {p.amount !== p.receipt_amount && ` (part of ${Number(p.receipt_amount).toFixed(2)})`}
                  </li>
                ))}
                {inv.credit_notes.map((c) => (
                  <li key={`c${c.id}`}>Credit note <DocLink kind="credit_note" id={c.id} number={c.number} /> — {money(inv.currency, c.amount)} ({c.status_display})</li>
                ))}
                {inv.payment_plans.map((p) => (
                  <li key={`pp${p.id}`}>
                    Payment plan <DocLink kind="payment_plan" id={p.id} number={p.number} /> ({p.status_display}):{' '}
                    {p.instalments.map((i) => `${i.label} ${Number(i.amount).toFixed(2)} — ${i.state.toLowerCase().replace('_', '-')}`).join(' · ')}
                  </li>
                ))}
                {inv.agreements.map((a) => (
                  <li key={`a${a.id}`}>Service agreement <DocLink kind="service_agreement" id={a.id} number={a.number} /> ({a.status_display})</li>
                ))}
                {inv.deadline_changes.length > 0 && <li><DeadlineHistory changes={inv.deadline_changes} /></li>}
                {!inv.payments.length && !inv.credit_notes.length && !inv.payment_plans.length && !inv.agreements.length
                  && <li style={{ color: '#94a3b8' }}>Nothing recorded against this invoice yet.</li>}
              </ul>
            </div>
          ))}

          {(data.not_for_invoice_receipts.length > 0 || data.agreements.length > 0 || data.forecasts.length > 0) && (
            <div className="billing-chain">
              <div className="billing-chain-head">Other documents</div>
              <ul>
                {data.not_for_invoice_receipts.map((r) => (
                  <li key={`n${r.id}`}>
                    Receipt <DocLink kind="receipt" id={r.id} number={r.number} /> — {money(r.currency, r.unallocated)} not for an
                    invoice: {r.reason}
                  </li>
                ))}
                {data.agreements.map((a) => (
                  <li key={`sa${a.id}`}>Service agreement <DocLink kind="service_agreement" id={a.id} number={a.number} /> ({a.status_display}) — no invoice linked</li>
                ))}
                {data.forecasts.map((f) => (
                  <li key={`f${f.id}`}>Budget forecast <DocLink kind="budget_forecast" id={f.id} number={f.number} /> ({f.status_display})</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// The match editor needs the receipt's full record (allocations, student).
function MatchReceiptLoader({ id, onSaved, onCancel }) {
  const [receipt, setReceipt] = useState(null);
  useEffect(() => { billingAPI.receipts.get(id).then((r) => setReceipt(r.data)).catch(() => {}); }, [id]);
  if (!receipt) return <div className="billing-loading">Loading…</div>;
  return <MatchEditor receipt={receipt} onSaved={onSaved} onCancel={onCancel} />;
}

function Receipts() {
  // Agents never sign billing documents — they go out through an employee/admin.
  // Founder rule, 19 Oct 2026: a receipt is a recorded payment, so only the CFO
  // creates or issues one (everyone internal can still sign and download).
  const { isAgent, canRecordPayments } = useAuth();
  const isRecorder = canRecordPayments();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState(emptyReceipt);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [me, setMe] = useState(null);
  const [signRole, setSignRole] = useState({});
  const [rowMsg, setRowMsg] = useState('');
  // Which invoice(s) the new receipt pays (founder, 24 Oct 2026: forced choice).
  const [openInv, setOpenInv] = useState([]);
  const [parts, setParts] = useState({});
  const [touched, setTouched] = useState(false);
  const [notForReason, setNotForReason] = useState('');
  const [matchingId, setMatchingId] = useState(null);

  useEffect(() => {
    setOpenInv([]); setParts({}); setTouched(false);
    if (!isRecorder || !form.student) return;
    billingAPI.receipts.openInvoices(form.student, form.currency || undefined)
      .then((r) => setOpenInv(r.data?.results || []))
      .catch(() => setOpenInv([]));
  }, [isRecorder, form.student, form.currency]);

  const formTotal = receiptLinesTotal(form.lines);
  // One open invoice is pre-selected with as much of the receipt as fits it.
  const effectiveParts = touched || openInv.length !== 1 ? parts
    : { [openInv[0].id]: Math.min(formTotal, Number(openInv[0].balance)).toFixed(2) };
  const appliedTotal = Object.values(effectiveParts).reduce((acc, v) => acc + (Number(v) || 0), 0);
  const restTotal = formTotal - appliedTotal;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingAPI.receipts.list();
      setRows(res.data?.results ?? res.data ?? []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    if (!isRecorder) {
      studentsAPI.list({ page_size: 500 }).then((r) => setStudents(r.data?.results ?? r.data ?? [])).catch(() => {});
    }
    usersAPI.me().then((r) => setMe(r.data)).catch(() => {});
  }, [load, isRecorder]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      const lines = form.lines
        .filter((l) => l.description.trim() || Number(l.amount))
        .map((l, i) => ({ description: l.description.trim() || 'Payment received', amount: l.amount || '0.00', order: i }));
      const payload = {
        ...form, lines, amount: receiptLinesTotal(lines).toFixed(2),
        for_description: lines.map((l) => l.description).join('; ').slice(0, 512),
        allocations: Object.entries(effectiveParts).filter(([, v]) => Number(v) > 0)
          .map(([invoice, amount]) => ({ invoice, amount: Number(amount).toFixed(2) })),
        not_for_invoice_reason: restTotal > 0.004 ? notForReason.trim() : '',
      };
      if (!payload.received_on) delete payload.received_on;
      await billingAPI.receipts.create(payload);
      setForm(emptyReceipt());
      setNotForReason('');
      load();
    } catch (err) {
      setError(errMsg(err, 'Create failed.'));
    } finally { setCreating(false); }
  };

  const issue = async (id) => {
    setRowMsg('');
    try {
      await billingAPI.receipts.issue(id);
      load();
    } catch (err) {
      setRowMsg(errMsg(err, 'Could not issue the receipt.'));
    }
  };

  const downloadPdf = async (r) => {
    const res = await billingAPI.receipts.pdf(r.id);
    downloadBlob(res, `${r.receipt_number || 'receipt'}.pdf`);
  };

  const applySignature = async (r) => {
    const role = (signRole[r.id] || '').trim();
    if (!role) return;
    if (!me?.signature_file) {
      setRowMsg('No signature on file. Upload one in Settings → My signature first.');
      return;
    }
    const fd = new FormData();
    fd.append('role_label', role);
    try {
      await billingAPI.receipts.applySignature(r.id, fd);
      setSignRole((s) => ({ ...s, [r.id]: '' }));
      setRowMsg('');
      load();
    } catch (err) {
      setRowMsg(err.response?.data ? JSON.stringify(err.response.data) : 'Could not apply signature.');
    }
  };

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Receipts</h3></div>
      <p className="billing-hint">
        {isRecorder
          ? 'Issue a payment receipt to a client, apply role signatures, and download the branded PDF. Every receipt says which invoice(s) it pays — or, for money that isn\'t for an invoice, why.'
          : 'Receipts are issued when the CFO or Chairman records a payment — only the CFO or Chairman records payments. You can apply your signature and download the PDF.'}
      </p>
      {rowMsg && <div className="billing-error">{rowMsg}</div>}

      {isRecorder && <form className="billing-form" onSubmit={create}>
        <div className="billing-form-grid">
          <label>Student
            {students.length ? (
              <select value={form.student} onChange={(e) => setForm({ ...form, student: e.target.value })} required>
                <option value="">Select…</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.first_name} ${s.last_name}`}</option>)}
              </select>
            ) : <ClientSearchSelect value={form.student} onChange={(v) => setForm({ ...form, student: v })} />}
          </label>
          <label>Currency
            <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </label>
          <label>Method
            <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Reference
            <input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} />
          </label>
          <label>Received on
            <input type="date" value={form.received_on} onChange={(e) => setForm({ ...form, received_on: e.target.value })} />
          </label>
        </div>
        <p className="billing-hint" style={{ margin: '10px 0 4px' }}>Items paid for — one line per item.</p>
        {form.lines.map((line, idx) => (
          <div className="billing-form-grid billing-receipt-line" key={idx}>
            <input placeholder="What it's for, e.g. Admission deposit" value={line.description}
              onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, description: e.target.value } : l)) }))} />
            <input type="number" step="0.01" min="0" placeholder="Amount" value={line.amount}
              onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, amount: e.target.value } : l)) }))} />
            {form.lines.length > 1 && (
              <button type="button" title="Remove item"
                onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))}>
                <FiTrash2 size={12} />
              </button>
            )}
          </div>
        ))}
        <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 8 }}
          onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, emptyReceiptLine()] }))}>+ Add item</button>
        <div className="billing-receipt-total">Total received: {form.currency} {receiptLinesTotal(form.lines).toFixed(2)}</div>
        {form.student && (
          <div className="billing-match" style={{ marginTop: 12 }}>
            <strong>Which invoice does this pay?</strong>
            {openInv.length === 0 && <div className="billing-hint" style={{ margin: '4px 0' }}>This client has no open {form.currency} invoice.</div>}
            {openInv.map((inv) => (
              <div className="billing-match-row" key={inv.id}>
                <input type="number" step="0.01" min="0" placeholder="0.00" value={effectiveParts[inv.id] ?? ''}
                  onChange={(e) => { setTouched(true); setParts({ ...effectiveParts, [inv.id]: e.target.value }); }} />
                <span>→ <strong>{inv.number}</strong> — {inv.currency} {Number(inv.balance).toFixed(2)} open
                  {inv.subject ? <span className="billing-sub" style={{ display: 'inline', marginLeft: 6 }}>{inv.subject}</span> : null}</span>
              </div>
            ))}
            {restTotal > 0.004 && (
              <div className="billing-match-row">
                <span>{appliedTotal > 0 ? `Rest (${form.currency} ${restTotal.toFixed(2)})` : 'Not for an invoice'} — why?</span>
                <input className="billing-wide" placeholder="e.g. Advance / credit on account" value={notForReason}
                  onChange={(e) => setNotForReason(e.target.value)} />
              </div>
            )}
            {restTotal < -0.004 && <div className="billing-error">The invoice amounts add up to more than the receipt.</div>}
          </div>
        )}
        {error && <div className="billing-error">{error}</div>}
        <div className="billing-form-actions">
          <button type="submit" className="btn-primary btn-sm"
            disabled={creating || formTotal <= 0 || restTotal < -0.004 || (restTotal > 0.004 && !notForReason.trim())}>
            {creating ? 'Saving…' : 'Create receipt'}
          </button>
        </div>
      </form>}

      {loading ? <div className="billing-loading">Loading…</div> : (
        <table className="billing-table">
          <thead>
            <tr><th>Number</th><th>Issued</th><th>Client</th><th>Items</th><th className="num">Amount</th><th>Method</th><th>Status</th><th>Signatures</th><th /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="billing-empty">No receipts yet.</td></tr>}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
              <tr>
                <td>{r.receipt_number || 'DRAFT'}</td>
                <td title={`Received ${fmtDate(r.received_on)}`}>{fmtDate(r.issued_at || r.created_at)}</td>
                <td>{r.student_name}</td>
                <td>
                  {(r.lines || []).length
                    ? r.lines.map((l) => <div key={l.id}>{l.description} — {Number(l.amount).toFixed(2)}</div>)
                    : (r.for_description || '—')}
                  {(r.allocations || []).length > 0 && (
                    <span className="billing-sub">
                      Applied to {r.allocations.map((a) => `${a.invoice_number} ${Number(a.amount).toFixed(2)}`).join(' · ')}
                    </span>
                  )}
                  {r.not_for_invoice_reason && Number(r.unallocated) > 0 && (
                    <span className="billing-sub">Not for an invoice ({Number(r.unallocated).toFixed(2)}): {r.not_for_invoice_reason}</span>
                  )}
                  {r.needs_matching && <span className="billing-pill billing-pill-amber">Not linked to an invoice</span>}
                </td>
                <td className="num">{r.currency} {Number(r.amount).toFixed(2)}</td>
                <td>{r.payment_method_display}</td>
                <td>{r.status_display}</td>
                <td>{(r.signatures || []).map((s) => s.role_label).join(', ') || '—'}</td>
                <td className="billing-row-actions">
                  {r.status === 'DRAFT' && isRecorder && <button onClick={() => issue(r.id)} title="Issue">Issue</button>}
                  {!isAgent() && (
                    <>
                      <input
                        type="text"
                        className="billing-sign-input"
                        placeholder="Sign as…"
                        value={signRole[r.id] || ''}
                        onChange={(e) => setSignRole((s) => ({ ...s, [r.id]: e.target.value }))}
                      />
                      <button onClick={() => applySignature(r)} disabled={!(signRole[r.id] || '').trim()} title="Apply my signature">Sign</button>
                    </>
                  )}
                  {r.status === 'ISSUED' && isRecorder && (
                    <button onClick={() => setMatchingId(matchingId === r.id ? null : r.id)}
                      title="Link this receipt to invoice(s), split it, or take it off one">Invoices…</button>
                  )}
                  <ClientFileButton student={r.student} />
                  <button onClick={() => downloadPdf(r)} title="Download PDF"><FiDownload size={13} /></button>
                </td>
              </tr>
              {matchingId === r.id && (
                <tr><td colSpan={9}>
                  <MatchEditor receipt={r} onSaved={() => { setMatchingId(null); load(); }} onCancel={() => setMatchingId(null)} />
                </td></tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const emptyForecastForm = () => ({
  student: '', tuitionKey: '', destination_city: '', destination_label: '', intake_label: '',
});
const emptyForecastFeeLine = () => ({
  section: 'A', description: '', amount_usd: '0.00', recurs_annually: false, escalation_pct: '0.00',
});
const emptyForecastLivingLine = () => ({ label: '', monthly_usd: '0.00', note: '' });

function Forecasts() {
  // Agents never sign billing documents — they go out through an employee/admin.
  const { isAgent, canIssueBillingDocs } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [lookups, setLookups] = useState({ universities: [], program_tuitions: [], cost_cities: [] });
  const [form, setForm] = useState(emptyForecastForm());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null); // forecast id being edited
  const [feeLines, setFeeLines] = useState([]);
  const [livingLines, setLivingLines] = useState([]);
  const [savingLines, setSavingLines] = useState(false);
  const [voidReason, setVoidReason] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [me, setMe] = useState(null);
  const [signRole, setSignRole] = useState({});
  const [rowMsg, setRowMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetForecastsAPI.list();
      setRows(res.data?.results ?? res.data ?? []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    studentsAPI.list({ page_size: 500 }).then((r) => setStudents(r.data?.results ?? r.data ?? [])).catch(() => {});
    budgetForecastsAPI.lookups().then((r) => setLookups(r.data)).catch(() => {});
    usersAPI.me().then((r) => setMe(r.data)).catch(() => {});
  }, [load]);

  const tuitionOptions = lookups.program_tuitions.map((pt) => {
    const uni = lookups.universities.find((u) => u.id === pt.university_id);
    return { ...pt, uniName: uni ? uni.name : `University #${pt.university_id}` };
  });

  const create = async (e) => {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      const pt = tuitionOptions.find((x) => String(x.id) === String(form.tuitionKey));
      const payload = {
        student: form.student,
        university: pt ? pt.university_id : undefined,
        program: pt ? pt.program_id : undefined,
        destination_city: form.destination_city || undefined,
        destination_label: form.destination_label,
        intake_label: form.intake_label,
      };
      await budgetForecastsAPI.create(payload);
      setForm(emptyForecastForm());
      load();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Create failed.');
    } finally { setCreating(false); }
  };

  const downloadPdf = async (row) => {
    const res = await budgetForecastsAPI.pdf(row.id);
    downloadBlob(res, `${row.number || 'budget-forecast'}.pdf`);
  };

  const issue = async (row) => {
    setBusyId(row.id);
    try {
      await budgetForecastsAPI.issue(row.id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not issue.');
    } finally { setBusyId(null); }
  };

  const voidForecast = async (row) => {
    const reason = (voidReason[row.id] || '').trim();
    if (!reason) return;
    setBusyId(row.id);
    try {
      await budgetForecastsAPI.void(row.id, reason);
      setVoidReason((s) => ({ ...s, [row.id]: '' }));
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not void.');
    } finally { setBusyId(null); }
  };

  const applySignature = async (row) => {
    const role = (signRole[row.id] || '').trim();
    if (!role) return;
    if (!me?.signature_file) {
      setRowMsg('No signature on file. Upload one in Settings → My signature first.');
      return;
    }
    const fd = new FormData();
    fd.append('role_label', role);
    try {
      await budgetForecastsAPI.applySignature(row.id, fd);
      setSignRole((s) => ({ ...s, [row.id]: '' }));
      setRowMsg('');
      load();
    } catch (err) {
      setRowMsg(err.response?.data ? JSON.stringify(err.response.data) : 'Could not apply signature.');
    }
  };

  const openLines = (row) => {
    if (expanded === row.id) { setExpanded(null); return; }
    setExpanded(row.id);
    setFeeLines(row.fee_lines && row.fee_lines.length ? row.fee_lines.map((l) => ({ ...l })) : [emptyForecastFeeLine()]);
    setLivingLines(row.living_lines && row.living_lines.length ? row.living_lines.map((l) => ({ ...l })) : [emptyForecastLivingLine()]);
  };

  const saveLines = async (row) => {
    setSavingLines(true);
    try {
      await budgetForecastsAPI.update(row.id, {
        fee_lines: feeLines.filter((l) => l.description.trim()),
        living_lines: livingLines.filter((l) => l.label.trim()),
      });
      setExpanded(null);
      load();
    } catch (err) {
      alert(err.response?.data ? JSON.stringify(err.response.data) : 'Could not save.');
    } finally { setSavingLines(false); }
  };

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Budget forecasts</h3></div>
      <p className="billing-hint">
        A whole-programme cost estimate for planning, sponsors, and visa / bank-proof purposes — not an invoice.
        Tuition, duration and living costs are pre-filled and stay editable before issuing.
      </p>
      {rowMsg && <div className="billing-error">{rowMsg}</div>}

      <form className="billing-form" onSubmit={create}>
        <div className="billing-form-grid">
          <label>Student
            <select value={form.student} onChange={(e) => setForm({ ...form, student: e.target.value })} required>
              <option value="">Select…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.first_name} ${s.last_name}`}</option>)}
            </select>
          </label>
          <label>University & programme
            <select value={form.tuitionKey} onChange={(e) => setForm({ ...form, tuitionKey: e.target.value })}>
              <option value="">No programme tuition on file yet…</option>
              {tuitionOptions.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.uniName} — {pt.program__name} ({pt.duration_years}y, USD {pt.annual_tuition_usd}/yr)
                </option>
              ))}
            </select>
          </label>
          <label>City (cost-of-living pre-fill)
            <select value={form.destination_city} onChange={(e) => setForm({ ...form, destination_city: e.target.value })}>
              <option value="">None — fill living costs manually</option>
              {lookups.cost_cities.map((c) => <option key={c.id} value={c.id}>{c.name}, {c.country}</option>)}
            </select>
          </label>
          <label>Destination label
            <input placeholder="e.g. Istanbul, Türkiye" value={form.destination_label}
              onChange={(e) => setForm({ ...form, destination_label: e.target.value })} />
          </label>
          <label>Intake
            <input placeholder="e.g. September 2027" value={form.intake_label}
              onChange={(e) => setForm({ ...form, intake_label: e.target.value })} />
          </label>
        </div>
        {error && <div className="billing-error">{error}</div>}
        <div className="billing-form-actions">
          <button type="submit" className="btn-primary btn-sm" disabled={creating}>{creating ? 'Creating…' : 'Create forecast'}</button>
        </div>
      </form>

      {loading ? <div className="billing-loading">Loading…</div> : (
        <table className="billing-table">
          <thead>
            <tr><th>Number</th><th>Client</th><th>Programme</th><th className="num">Programme total</th><th>Status</th><th>Signatures</th><th /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="billing-empty">No forecasts yet.</td></tr>}
            {rows.map((f) => (
              <React.Fragment key={f.id}>
                <tr>
                  <td>{f.number || 'DRAFT'}</td>
                  <td>{f.student_name}</td>
                  <td>{f.university_display}{f.duration_years ? ` — ${f.duration_years}y` : ''}</td>
                  <td className="num">USD {Number(f.programme_total).toFixed(2)}</td>
                  <td>{f.status_display}</td>
                  <td>{(f.signatures || []).map((s) => s.role_label).join(', ') || '—'}</td>
                  <td className="billing-row-actions">
                    {f.status === 'DRAFT' && (
                      <button onClick={() => openLines(f)} title="Edit lines">
                        {expanded === f.id ? 'Close' : 'Edit lines'}
                      </button>
                    )}
                    {f.status === 'DRAFT' && (canIssueBillingDocs() ? (
                      <button onClick={() => issue(f)} disabled={busyId === f.id} title="Issue">
                        {busyId === f.id ? 'Issuing…' : 'Issue'}
                      </button>
                    ) : <span style={ISSUER_NOTE_STYLE} title={ISSUER_NOTE}>Draft — awaiting issuer</span>)}
                    {f.status !== 'DRAFT' && f.status !== 'VOID' && !isAgent() && (
                      <>
                        <input
                          type="text"
                          className="billing-sign-input"
                          placeholder="Sign as…"
                          value={signRole[f.id] || ''}
                          onChange={(e) => setSignRole((s) => ({ ...s, [f.id]: e.target.value }))}
                        />
                        <button onClick={() => applySignature(f)} disabled={!(signRole[f.id] || '').trim()} title="Apply my signature">Sign</button>
                      </>
                    )}
                    <button onClick={() => downloadPdf(f)} title="Download PDF"><FiDownload size={13} /></button>
                  </td>
                </tr>
                {expanded === f.id && (
                  <tr>
                    <td colSpan={7}>
                      <div className="billing-form">
                        <strong>Sections A/B — fee lines</strong>
                        {feeLines.map((l, idx) => (
                          <div className="billing-form-grid" key={idx} style={{ marginTop: 8 }}>
                            <select value={l.section} onChange={(e) => setFeeLines((ls) => ls.map((x, i) => i === idx ? { ...x, section: e.target.value } : x))}>
                              <option value="A">A — VisionExploit fee</option>
                              <option value="B">B — Official cost</option>
                            </select>
                            <input placeholder="Description" value={l.description}
                              onChange={(e) => setFeeLines((ls) => ls.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} />
                            <input type="number" step="0.01" placeholder="Amount (year 1)" value={l.amount_usd}
                              onChange={(e) => setFeeLines((ls) => ls.map((x, i) => i === idx ? { ...x, amount_usd: e.target.value } : x))} />
                            <label className="billing-check">
                              <input type="checkbox" checked={!!l.recurs_annually}
                                onChange={(e) => setFeeLines((ls) => ls.map((x, i) => i === idx ? { ...x, recurs_annually: e.target.checked } : x))} />
                              Recurs yearly
                            </label>
                            <button type="button" onClick={() => setFeeLines((ls) => ls.filter((_, i) => i !== idx))}><FiTrash2 size={12} /></button>
                          </div>
                        ))}
                        <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 8 }}
                          onClick={() => setFeeLines((ls) => [...ls, emptyForecastFeeLine()])}>+ Add fee line</button>

                        <strong style={{ display: 'block', marginTop: 16 }}>Section D — living cost lines (monthly, year 1)</strong>
                        {livingLines.map((l, idx) => (
                          <div className="billing-form-grid" key={idx} style={{ marginTop: 8 }}>
                            <input placeholder="Label, e.g. Accommodation" value={l.label}
                              onChange={(e) => setLivingLines((ls) => ls.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} />
                            <input type="number" step="0.01" placeholder="Monthly USD" value={l.monthly_usd}
                              onChange={(e) => setLivingLines((ls) => ls.map((x, i) => i === idx ? { ...x, monthly_usd: e.target.value } : x))} />
                            <input placeholder="Note (optional)" value={l.note || ''}
                              onChange={(e) => setLivingLines((ls) => ls.map((x, i) => i === idx ? { ...x, note: e.target.value } : x))} />
                            <button type="button" onClick={() => setLivingLines((ls) => ls.filter((_, i) => i !== idx))}><FiTrash2 size={12} /></button>
                          </div>
                        ))}
                        <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 8 }}
                          onClick={() => setLivingLines((ls) => [...ls, emptyForecastLivingLine()])}>+ Add living-cost line</button>

                        <div className="billing-form-actions" style={{ marginTop: 12 }}>
                          <button type="button" className="btn-primary btn-sm" disabled={savingLines} onClick={() => saveLines(f)}>
                            {savingLines ? 'Saving…' : 'Save lines'}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {f.status === 'ISSUED' && (
                  <tr>
                    <td colSpan={7} className="billing-row-actions" style={{ textAlign: 'left' }}>
                      <input type="text" className="billing-sign-input" placeholder="Reason to void" style={{ width: 200 }}
                        value={voidReason[f.id] || ''} onChange={(e) => setVoidReason((s) => ({ ...s, [f.id]: e.target.value }))} />
                      <button onClick={() => voidForecast(f)} disabled={busyId === f.id || !(voidReason[f.id] || '').trim()}>Void</button>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const emptyAgreementForm = () => ({
  student: '', destination_label: 'Türkiye', max_applications: 3, service_fee_usd: '600.00', deposit_usd: '30.00',
  has_sponsor: false, sponsor_name: '', sponsor_relationship: '', sponsor_id_number: '', sponsor_phone: '', sponsor_email: '',
  requires_guardian: false, guardian_name: '',
});
const emptyAddon = () => ({ description: '', note: '', price_usd: '0.00', selected: false });

// Shown after "Send for signature" — one row per party (client/sponsor/
// guardian) with a copy-link button, a WhatsApp button when we have a phone
// on file, and whether the auto-email went out.
function SigningLinks({ links }) {
  const [copied, setCopied] = useState(null);
  if (!links || !links.length) return null;
  const copy = (url, id) => {
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 1500);
  };
  return (
    <div className="billing-form" style={{ marginTop: 8 }}>
      <strong>Signing links</strong>
      {links.map((l) => (
        <div key={l.id} className="billing-form-grid" style={{ marginTop: 6, alignItems: 'center' }}>
          <span>{l.role_display}{l.expected_name ? ` — ${l.expected_name}` : ''}</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => copy(l.sign_url, l.id)}>
            {copied === l.id ? 'Copied!' : 'Copy link'}
          </button>
          {l.whatsapp_url ? (
            <a className="btn-secondary btn-sm" href={l.whatsapp_url} target="_blank" rel="noreferrer"
              style={{ textAlign: 'center', textDecoration: 'none' }}>
              Open WhatsApp
            </a>
          ) : <span className="billing-sub">No phone on file — share the link manually</span>}
          <span className="billing-sub">
            {l.emailed ? (l.emailed.sent ? `Emailed: ${l.emailed.detail}` : l.emailed.detail) : 'No email sent'}
          </span>
        </div>
      ))}
    </div>
  );
}

function Agreements() {
  const { isChairman, canIssueBillingDocs } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState(emptyAgreementForm());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [addons, setAddons] = useState([]);
  const [savingAddons, setSavingAddons] = useState(false);
  const [voidReason, setVoidReason] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [links, setLinks] = useState({});
  const [sending, setSending] = useState(null);

  const sendForSignature = async (row) => {
    setSending(row.id);
    try {
      const r = await signingAPI.send('service-agreements', row.id);
      setLinks((s) => ({ ...s, [row.id]: r.data }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not send for signature.');
    } finally { setSending(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceAgreementsAPI.list();
      setRows(res.data?.results ?? res.data ?? []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    studentsAPI.list({ page_size: 500 }).then((r) => setStudents(r.data?.results ?? r.data ?? [])).catch(() => {});
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      await serviceAgreementsAPI.create(form);
      setForm(emptyAgreementForm());
      load();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Create failed.');
    } finally { setCreating(false); }
  };

  const downloadPdf = async (row) => {
    const res = await serviceAgreementsAPI.pdf(row.id);
    downloadBlob(res, `${row.number || 'service-agreement'}.pdf`);
  };

  const toggleConsent = async (row) => {
    setBusyId(row.id);
    try {
      await serviceAgreementsAPI.update(row.id, { consent_data_transfer: true });
      load();
    } catch (err) {
      alert(err.response?.data ? JSON.stringify(err.response.data) : 'Could not record consent.');
    } finally { setBusyId(null); }
  };

  const issue = async (row) => {
    setBusyId(row.id);
    try {
      await serviceAgreementsAPI.issue(row.id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || (err.response?.data ? JSON.stringify(err.response.data) : 'Could not issue.'));
    } finally { setBusyId(null); }
  };

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      await serviceAgreementsAPI.markLawyerApproved(row.id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not mark as lawyer-approved.');
    } finally { setBusyId(null); }
  };

  const voidAgreement = async (row) => {
    const reason = (voidReason[row.id] || '').trim();
    if (!reason) return;
    setBusyId(row.id);
    try {
      await serviceAgreementsAPI.void(row.id, reason);
      setVoidReason((s) => ({ ...s, [row.id]: '' }));
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not void.');
    } finally { setBusyId(null); }
  };

  const openAddons = (row) => {
    if (expanded === row.id) { setExpanded(null); return; }
    setExpanded(row.id);
    setAddons(row.addons && row.addons.length ? row.addons.map((x) => ({ ...x })) : [emptyAddon()]);
  };

  const saveAddons = async (row) => {
    setSavingAddons(true);
    try {
      await serviceAgreementsAPI.update(row.id, { addons: addons.filter((x) => x.description.trim()) });
      setExpanded(null);
      load();
    } catch (err) {
      alert(err.response?.data ? JSON.stringify(err.response.data) : 'Could not save.');
    } finally { setSavingAddons(false); }
  };

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Service agreements</h3></div>
      <p className="billing-hint">
        Ships marked <strong>DRAFT — pending legal review</strong> until the Chairman marks the text lawyer-approved.
        The client and sponsor sign remotely once e-signing is live — for now, apply your own signature below.
      </p>

      <form className="billing-form" onSubmit={create}>
        <div className="billing-form-grid">
          <label>Student
            <select value={form.student} onChange={(e) => setForm({ ...form, student: e.target.value })} required>
              <option value="">Select…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.first_name} ${s.last_name}`}</option>)}
            </select>
          </label>
          <label>Destination
            <input value={form.destination_label} onChange={(e) => setForm({ ...form, destination_label: e.target.value })} />
          </label>
          <label>Max applications
            <input type="number" value={form.max_applications} onChange={(e) => setForm({ ...form, max_applications: e.target.value })} />
          </label>
          <label>Service fee (USD)
            <input type="number" step="0.01" value={form.service_fee_usd} onChange={(e) => setForm({ ...form, service_fee_usd: e.target.value })} />
          </label>
          <label>Deposit (USD)
            <input type="number" step="0.01" value={form.deposit_usd} onChange={(e) => setForm({ ...form, deposit_usd: e.target.value })} />
          </label>
          <label className="billing-check">
            <input type="checkbox" checked={form.has_sponsor} onChange={(e) => setForm({ ...form, has_sponsor: e.target.checked })} />
            Fees are paid by a sponsor
          </label>
          <label className="billing-check">
            <input type="checkbox" checked={form.requires_guardian} onChange={(e) => setForm({ ...form, requires_guardian: e.target.checked })} />
            Client is under 18 (needs a guardian)
          </label>
        </div>
        {form.has_sponsor && (
          <div className="billing-form-grid" style={{ marginTop: 8 }}>
            <input placeholder="Sponsor name" value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })} />
            <input placeholder="Relationship, e.g. father" value={form.sponsor_relationship} onChange={(e) => setForm({ ...form, sponsor_relationship: e.target.value })} />
            <input placeholder="ID / passport no." value={form.sponsor_id_number} onChange={(e) => setForm({ ...form, sponsor_id_number: e.target.value })} />
            <input placeholder="Phone" value={form.sponsor_phone} onChange={(e) => setForm({ ...form, sponsor_phone: e.target.value })} />
            <input placeholder="Email" value={form.sponsor_email} onChange={(e) => setForm({ ...form, sponsor_email: e.target.value })} />
          </div>
        )}
        {form.requires_guardian && (
          <div className="billing-form-grid" style={{ marginTop: 8 }}>
            <input placeholder="Guardian name" value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} />
          </div>
        )}
        {error && <div className="billing-error">{error}</div>}
        <div className="billing-form-actions">
          <button type="submit" className="btn-primary btn-sm" disabled={creating}>{creating ? 'Creating…' : 'Create agreement'}</button>
        </div>
      </form>

      {loading ? <div className="billing-loading">Loading…</div> : (
        <table className="billing-table">
          <thead>
            <tr><th>Number</th><th>Client</th><th className="num">Fee</th><th>Status</th><th>Legal</th><th /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="billing-empty">No agreements yet.</td></tr>}
            {rows.map((a) => (
              <React.Fragment key={a.id}>
                <tr>
                  <td>{a.number || 'DRAFT'}</td>
                  <td>{a.student_name}</td>
                  <td className="num">USD {Number(a.service_fee_usd).toFixed(2)}</td>
                  <td>{a.status_display}</td>
                  <td>{a.lawyer_approved ? 'Approved' : 'Pending review'}</td>
                  <td className="billing-row-actions">
                    {a.status === 'DRAFT' && (
                      <button onClick={() => openAddons(a)} title="Edit add-ons">
                        {expanded === a.id ? 'Close' : 'Add-ons'}
                      </button>
                    )}
                    {a.status === 'DRAFT' && !a.consent_data_transfer && (
                      <button onClick={() => toggleConsent(a)} disabled={busyId === a.id} title="Record required consent">
                        Record consent
                      </button>
                    )}
                    {a.status === 'DRAFT' && (canIssueBillingDocs() ? (
                      <button onClick={() => issue(a)} disabled={busyId === a.id} title="Issue">
                        {busyId === a.id ? 'Issuing…' : 'Issue'}
                      </button>
                    ) : <span style={ISSUER_NOTE_STYLE} title={ISSUER_NOTE}>Draft — awaiting issuer</span>)}
                    {!a.lawyer_approved && isChairman() && (
                      <button onClick={() => approve(a)} disabled={busyId === a.id} title="Mark lawyer-approved">
                        Mark approved
                      </button>
                    )}
                    {a.status === 'ISSUED' && (
                      <button onClick={() => sendForSignature(a)} disabled={sending === a.id}
                        title={canIssueBillingDocs() ? 'Send for signature' : 'Resend the signing links an issuer already sent'}>
                        {sending === a.id ? 'Sending…' : (canIssueBillingDocs() ? 'Send for signature' : 'Resend signing links')}
                      </button>
                    )}
                    <button onClick={() => downloadPdf(a)} title="Download PDF"><FiDownload size={13} /></button>
                  </td>
                </tr>
                {links[a.id] && (
                  <tr><td colSpan={6}><SigningLinks links={links[a.id]} /></td></tr>
                )}
                {expanded === a.id && (
                  <tr>
                    <td colSpan={6}>
                      <div className="billing-form">
                        <strong>Optional add-on services</strong>
                        {addons.map((x, idx) => (
                          <div className="billing-form-grid" key={idx} style={{ marginTop: 8 }}>
                            <label className="billing-check">
                              <input type="checkbox" checked={!!x.selected}
                                onChange={(e) => setAddons((ls) => ls.map((v, i) => i === idx ? { ...v, selected: e.target.checked } : v))} />
                              Client is buying this
                            </label>
                            <input placeholder="Description" value={x.description}
                              onChange={(e) => setAddons((ls) => ls.map((v, i) => i === idx ? { ...v, description: e.target.value } : v))} />
                            <input type="number" step="0.01" placeholder="Price (USD)" value={x.price_usd}
                              onChange={(e) => setAddons((ls) => ls.map((v, i) => i === idx ? { ...v, price_usd: e.target.value } : v))} />
                            <button type="button" onClick={() => setAddons((ls) => ls.filter((_, i) => i !== idx))}><FiTrash2 size={12} /></button>
                          </div>
                        ))}
                        <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 8 }}
                          onClick={() => setAddons((ls) => [...ls, emptyAddon()])}>+ Add add-on</button>
                        <div className="billing-form-actions" style={{ marginTop: 12 }}>
                          <button type="button" className="btn-primary btn-sm" disabled={savingAddons} onClick={() => saveAddons(a)}>
                            {savingAddons ? 'Saving…' : 'Save add-ons'}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {a.status === 'ISSUED' && (
                  <tr>
                    <td colSpan={6} className="billing-row-actions" style={{ textAlign: 'left' }}>
                      <input type="text" className="billing-sign-input" placeholder="Reason to void" style={{ width: 200 }}
                        value={voidReason[a.id] || ''} onChange={(e) => setVoidReason((s) => ({ ...s, [a.id]: e.target.value }))} />
                      <button onClick={() => voidAgreement(a)} disabled={busyId === a.id || !(voidReason[a.id] || '').trim()}>Void</button>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const emptyQuoteLine = () => ({
  section: 'A', description: '', quantity: '1', unit_price: '0.00',
  tax_rate_pct: '0.00', price_item: '', paid_to: '',
});
const emptyQuoteForm = () => ({
  student: '', kind: 'INVOICE', currency: 'USD', subject: '', due_date: '', valid_until: '',
  arrival_date: '', terms: '', notes: '', lines: [emptyQuoteLine()],
});

// Quotations & Invoices, reachable directly from Billing Documents — same
// documents Deals.js can create for a specific deal, but student-scoped
// instead of deal-scoped, for the (common) case where staff just need to
// bill a student without first building out a pipeline deal for them.
const SHOW_FILTERS = [
  ['all', 'All quotations & invoices'], ['quotations', 'Quotations'], ['unpaid', 'Invoices — unpaid / overdue'],
  ['part', 'Invoices — part-paid'], ['paid', 'Invoices — paid'], ['drafts', 'Drafts'],
];
const SHOW_TEST = {
  all: () => true,
  quotations: (q) => q.kind === 'QUOTATION',
  unpaid: (q) => q.kind === 'INVOICE' && ['UNPAID', 'OVERDUE'].includes(q.payment_status),
  part: (q) => q.kind === 'INVOICE' && q.payment_status === 'PART_PAID',
  paid: (q) => q.kind === 'INVOICE' && q.payment_status === 'PAID',
  drafts: (q) => q.status === 'DRAFT',
};

function QuotationsInvoices() {
  const { isAgent, canIssueBillingDocs, canRecordPayments } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState(emptyQuoteForm());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [signRole, setSignRole] = useState({});
  const [voidReason, setVoidReason] = useState({});
  const [payForm, setPayForm] = useState({});
  const [links, setLinks] = useState({});
  const [show, setShow] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await quotationsAPI.list();
      setRows(res.data?.results ?? res.data ?? []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    studentsAPI.list({ page_size: 500 }).then((r) => setStudents(r.data?.results ?? r.data ?? [])).catch(() => {});
    billingAPI.priceItems.list({ is_active: true }).then((r) => setPriceItems(r.data?.results ?? r.data ?? [])).catch(() => {});
    usersAPI.me().then((r) => setMe(r.data)).catch(() => {});
  }, [load]);

  const handleLineChange = (idx, field, val) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, [field]: val } : l)) }));
  };

  const applyPriceItem = (idx, itemId) => {
    const item = priceItems.find((p) => String(p.id) === String(itemId));
    if (!item) return;
    setForm((f) => {
      const lines = [...f.lines];
      lines[idx] = {
        ...lines[idx], price_item: item.id,
        description: item.description ? `${item.name} — ${item.description}` : item.name,
        unit_price: item.default_unit_price, tax_rate_pct: item.tax_rate_pct,
      };
      return { ...f, lines };
    });
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      const payload = {
        student: form.student, kind: form.kind, currency: form.currency, subject: form.subject,
        due_date: form.due_date || null, valid_until: form.valid_until || null, arrival_date: form.arrival_date || null,
        terms: form.terms, notes: form.notes,
        lines: form.lines.filter((l) => l.description.trim()).map((l, i) => ({
          section: l.section || 'A', order: i + 1, description: l.description,
          quantity: l.quantity, unit_price: l.unit_price, tax_rate_pct: l.tax_rate_pct || '0.00',
          paid_to: l.section === 'B' ? (l.paid_to || '') : '',
          ...(l.price_item ? { price_item: l.price_item } : {}),
        })),
      };
      await quotationsAPI.create(payload);
      setForm(emptyQuoteForm());
      load();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Create failed.');
    } finally { setCreating(false); }
  };

  const downloadPdf = async (q) => {
    const res = await quotationsAPI.pdf(q.id);
    downloadBlob(res, `${q.quote_number || 'quotation'}.pdf`);
  };

  const issue = async (q) => {
    setBusyId(q.id);
    try {
      await quotationsAPI.issue(q.id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || (err.response?.data ? JSON.stringify(err.response.data) : 'Could not issue.'));
    } finally { setBusyId(null); }
  };

  const voidDoc = async (q) => {
    const reason = (voidReason[q.id] || '').trim();
    if (!reason) return;
    setBusyId(q.id);
    try {
      await quotationsAPI.void(q.id, reason);
      setVoidReason((s) => ({ ...s, [q.id]: '' }));
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not void.');
    } finally { setBusyId(null); }
  };

  const recordPayment = async (q) => {
    const pf = payForm[q.id] || {};
    if (!pf.amount) return;
    setBusyId(q.id);
    try {
      await quotationsAPI.recordPayment(q.id, {
        amount: pf.amount, payment_method: pf.method || 'CASH', payment_reference: pf.reference || '',
      });
      setPayForm((s) => ({ ...s, [q.id]: { amount: '', method: 'CASH', reference: '' } }));
      load();
    } catch (err) {
      alert(errMsg(err, 'Could not record the payment.'));
    } finally { setBusyId(null); }
  };

  const createInvoiceFromQuote = async (q) => {
    try {
      await quotationsAPI.createInvoice(q.id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not create an invoice from this quotation.');
    }
  };

  const sendForSignature = async (q) => {
    setBusyId(q.id);
    try {
      const r = await signingAPI.send('quotations', q.id);
      setLinks((s) => ({ ...s, [q.id]: r.data }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not create the acceptance link.');
    } finally { setBusyId(null); }
  };

  const applySignature = async (q) => {
    const role = (signRole[q.id] || '').trim();
    if (!role) return;
    if (!me?.signature_file) {
      setError('No signature on file. Upload one in Settings → My signature first.');
      return;
    }
    const fd = new FormData();
    fd.append('role_label', role);
    try {
      await quotationsAPI.applySignature(q.id, fd);
      setSignRole((s) => ({ ...s, [q.id]: '' }));
      setError('');
      load();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Could not apply signature.');
    }
  };

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Quotations & invoices</h3></div>
      <p className="billing-hint">
        Bill a student directly — no need to open a Deal first. (Quotations/invoices created from a Deal's
        own page still work exactly the same and show up here too.)
      </p>
      {error && <div className="billing-error">{error}</div>}

      <form className="billing-form" onSubmit={create}>
        <div className="billing-form-grid">
          <label>Student
            <select value={form.student} onChange={(e) => setForm({ ...form, student: e.target.value })} required>
              <option value="">Select…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.first_name} ${s.last_name}`}</option>)}
            </select>
          </label>
          <label>Document
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="QUOTATION">Quotation</option>
              <option value="INVOICE">Invoice</option>
            </select>
          </label>
          <label>Currency
            <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </label>
          <label>{form.kind === 'INVOICE' ? 'Due date' : 'Valid until'}
            <input type="date"
              value={form.kind === 'INVOICE' ? form.due_date : form.valid_until}
              onChange={(e) => setForm({ ...form, [form.kind === 'INVOICE' ? 'due_date' : 'valid_until']: e.target.value })} />
          </label>
          <label>Arrival date
            <input type="date" value={form.arrival_date} onChange={(e) => setForm({ ...form, arrival_date: e.target.value })} />
          </label>
        </div>
        <label className="billing-full">Re: (subject)
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="e.g. Arrival & Settlement Package — airport pickup, accommodation setup, residence-permit assistance" />
        </label>
        <p className="billing-hint">
          Section A = VisionExploit fees (collected). Section B = third-party estimates (not collected — e.g.
          tuition paid straight to the university). Pick a service from the price book to fill a Section A line.
        </p>
        {form.lines.map((line, idx) => (
          <div className="billing-form-grid" key={idx}>
            <select value={line.section} onChange={(e) => handleLineChange(idx, 'section', e.target.value)} style={{ maxWidth: 64 }}>
              <option value="A">A</option>
              <option value="B">B</option>
            </select>
            {priceItems.length > 0 && line.section === 'A' && (
              <select value={line.price_item || ''} onChange={(e) => applyPriceItem(idx, e.target.value)} style={{ maxWidth: 160 }}>
                <option value="">From price book…</option>
                {priceItems.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.currency} {p.default_unit_price})</option>)}
              </select>
            )}
            <input placeholder="Description" value={line.description}
              onChange={(e) => handleLineChange(idx, 'description', e.target.value)} />
            {line.section === 'B' ? (
              <input placeholder="Paid to (third party)" value={line.paid_to}
                onChange={(e) => handleLineChange(idx, 'paid_to', e.target.value)} />
            ) : (
              <input type="number" step="0.01" placeholder="Qty" value={line.quantity}
                onChange={(e) => handleLineChange(idx, 'quantity', e.target.value)} />
            )}
            <input type="number" step="0.01" placeholder="Unit price" value={line.unit_price}
              onChange={(e) => handleLineChange(idx, 'unit_price', e.target.value)} />
            <button type="button" onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))}>
              <FiTrash2 size={12} />
            </button>
          </div>
        ))}
        <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 8 }}
          onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, emptyQuoteLine()] }))}>+ Add line</button>
        <label className="billing-full">Notes & terms (leave blank to use the template default)
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="One term per line. Lines starting with '- ' render as bullets." />
        </label>
        <div className="billing-form-actions">
          <button type="submit" className="btn-primary btn-sm" disabled={creating}>
            {creating ? 'Saving…' : `Create ${form.kind === 'INVOICE' ? 'invoice' : 'quotation'}`}
          </button>
        </div>
      </form>

      <div className="billing-form-grid" style={{ maxWidth: 260, marginBottom: 10 }}>
        <select value={show} onChange={(e) => setShow(e.target.value)} aria-label="Show">
          {SHOW_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      {loading ? <div className="billing-loading">Loading…</div> : (
        <table className="billing-table">
          <thead>
            <tr><th>Number</th><th>Issued</th><th>Client</th><th>Document</th><th className="num">Amount</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {rows.filter(SHOW_TEST[show]).length === 0 && <tr><td colSpan={7} className="billing-empty">Nothing to show.</td></tr>}
            {rows.filter(SHOW_TEST[show]).map((q) => (
              <React.Fragment key={q.id}>
                <tr>
                  <td>{q.quote_number || 'DRAFT'}</td>
                  <td title={q.kind === 'INVOICE' ? `Due ${fmtDate(q.due_date)}` : `Valid until ${fmtDate(q.valid_until)}`}>
                    {fmtDate(q.issue_date)}
                  </td>
                  <td>{q.student_name}</td>
                  <td>{q.kind === 'INVOICE' ? 'Invoice' : 'Quotation'}</td>
                  <td className="num">
                    {q.currency} {Number(q.amount_due).toFixed(2)}
                    {q.kind === 'INVOICE' && q.status === 'ISSUED' && <> · Bal {Number(q.balance).toFixed(2)}</>}
                  </td>
                  <td>
                    {q.kind === 'INVOICE' && q.status === 'ISSUED' ? <PayPill status={q.payment_status} /> : (q.status_display || q.status)}
                  </td>
                  <td className="billing-row-actions">
                    {q.status === 'DRAFT' && (canIssueBillingDocs() ? (
                      <button onClick={() => issue(q)} disabled={busyId === q.id} title="Issue">
                        {busyId === q.id ? 'Issuing…' : (q.kind === 'INVOICE' ? 'Confirm & issue' : 'Send quotation')}
                      </button>
                    ) : <span style={ISSUER_NOTE_STYLE} title={ISSUER_NOTE}>Draft — awaiting issuer</span>)}
                    {q.kind === 'QUOTATION' && q.status === 'SENT' && !isAgent() && (
                      <button onClick={() => sendForSignature(q)} disabled={busyId === q.id} title="Send for client acceptance">
                        Send for client acceptance
                      </button>
                    )}
                    {q.kind === 'QUOTATION' && q.status === 'ACCEPTED' && (
                      <button onClick={() => createInvoiceFromQuote(q)} title="Create invoice from this quote">
                        Create invoice
                      </button>
                    )}
                    <ClientFileButton student={q.student} />
                    <button onClick={() => downloadPdf(q)} title="Download PDF"><FiDownload size={13} /></button>
                  </td>
                </tr>
                {q.kind === 'INVOICE' && ((q.payments || []).length > 0 || (q.deadline_changes || []).length > 0) && (
                  <tr><td colSpan={7} className="billing-applied">
                    <PaymentsReceived invoice={q} />
                    {(q.payments || []).length > 0 && (q.deadline_changes || []).length > 0 && <br />}
                    <DeadlineHistory changes={q.deadline_changes} />
                  </td></tr>
                )}
                {q.status === 'DECLINED' && q.decline_info && (
                  <tr><td colSpan={7} className="billing-declined">
                    Declined online{q.decline_info.by ? ` by ${q.decline_info.by}` : ''}
                    {q.decline_info.reason ? `: "${q.decline_info.reason}"` : ' (no reason given)'}
                  </td></tr>
                )}
                {links[q.id] && <tr><td colSpan={7}><SigningLinks links={links[q.id]} /></td></tr>}
                {q.kind === 'INVOICE' && q.status === 'ISSUED' && Number(q.balance) > 0 && !canRecordPayments() && (
                  <tr><td colSpan={7} style={ISSUER_NOTE_STYLE}>
                    Balance {q.currency} {Number(q.balance).toFixed(2)} outstanding. Only the CFO or Chairman records payments.
                  </td></tr>
                )}
                {q.kind === 'INVOICE' && q.status === 'ISSUED' && Number(q.balance) > 0 && canRecordPayments() && (
                  <tr>
                    <td colSpan={7} className="billing-row-actions" style={{ textAlign: 'left' }}>
                      <input type="number" step="0.01" placeholder={`Amount (≤ ${q.balance})`} style={{ maxWidth: 120 }}
                        value={(payForm[q.id] || {}).amount || ''}
                        onChange={(e) => setPayForm((s) => ({ ...s, [q.id]: { ...s[q.id], amount: e.target.value } }))} />
                      <select value={(payForm[q.id] || {}).method || 'CASH'}
                        onChange={(e) => setPayForm((s) => ({ ...s, [q.id]: { ...s[q.id], method: e.target.value } }))}>
                        {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <button onClick={() => recordPayment(q)} disabled={busyId === q.id || !(payForm[q.id] || {}).amount}>
                        Record payment
                      </button>
                    </td>
                  </tr>
                )}
                {q.status !== 'DRAFT' && q.status !== 'VOID' && !isAgent() && (
                  <tr>
                    <td colSpan={7} className="billing-row-actions" style={{ textAlign: 'left' }}>
                      <input type="text" className="billing-sign-input" placeholder="Sign as…"
                        value={signRole[q.id] || ''} onChange={(e) => setSignRole((s) => ({ ...s, [q.id]: e.target.value }))} />
                      <button onClick={() => applySignature(q)} disabled={!(signRole[q.id] || '').trim()}>Apply my signature</button>
                      {q.status !== 'VOID' && (
                        <>
                          <input type="text" className="billing-sign-input" placeholder="Reason to void" style={{ width: 160 }}
                            value={voidReason[q.id] || ''} onChange={(e) => setVoidReason((s) => ({ ...s, [q.id]: e.target.value }))} />
                          <button onClick={() => voidDoc(q)} disabled={busyId === q.id || !(voidReason[q.id] || '').trim()}>Void</button>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Unpaid invoices (most overdue first) and quotations close to or past their
// valid-until date. The same rules feed the daily bell reminders
// (backend deals/reminders.py) to the document's creator, the CFO and Chairman.
function Outstanding() {
  const { canRecordPayments } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [matching, setMatching] = useState(false);
  const [deadlineFor, setDeadlineFor] = useState(null);

  const load = useCallback(() => {
    billingAPI.outstanding()
      .then((r) => setData(r.data))
      .catch(() => setError('Could not load outstanding documents.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const statement = async (row) => {
    const res = await statementsAPI.pdf(row.student);
    downloadBlob(res, `statement-${row.student}.pdf`);
  };
  const docPdf = async (row) => {
    const res = await quotationsAPI.pdf(row.id);
    downloadBlob(res, `${row.number || 'document'}.pdf`);
  };

  if (error) return <div className="billing-panel"><div className="billing-error">{error}</div></div>;
  if (!data) return <div className="billing-panel"><div className="billing-loading">Loading…</div></div>;

  const overdue = data.invoices.filter((r) => r.days_overdue > 0);
  const owed = data.invoices.reduce((acc, r) => ({ ...acc, [r.currency]: (acc[r.currency] || 0) + Number(r.balance) }), {});

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Outstanding</h3></div>
      <p className="billing-hint">
        Invoices with money still owed and quotations about to expire. Overdue invoices are reminded in the bell
        every 7 days until paid — saying how much is left; quotations 3 days before and on the day they expire.
      </p>
      {data.unmatched_receipts > 0 && (
        <div className="billing-banner">
          <span>
            <strong>{data.unmatched_receipts}</strong> receipt{data.unmatched_receipts === 1 ? " isn't" : "s aren't"} linked
            to an invoice, so {data.unmatched_receipts === 1 ? 'its invoice may' : 'their invoices may'} still show as unpaid here.
          </span>
          <button type="button" onClick={() => setMatching((v) => !v)}>{matching ? 'Hide' : 'Match receipts'}</button>
        </div>
      )}
      {matching && <MatchReceipts onChanged={load} />}
      <div className="billing-summary">
        <div><strong>{data.invoices.length}</strong> unpaid invoice{data.invoices.length === 1 ? '' : 's'}</div>
        <div className={overdue.length ? 'billing-summary-alert' : ''}><strong>{overdue.length}</strong> overdue</div>
        <div>
          Owed: {Object.keys(owed).length
            ? Object.entries(owed).map(([cur, v]) => `${cur} ${v.toFixed(2)}`).join(' · ') : '—'}
        </div>
      </div>

      <h4>Unpaid invoices</h4>
      <table className="billing-table">
        <thead>
          <tr><th>Invoice</th><th>Client</th><th>Issued</th><th>Due</th><th className="num">Total</th>
            <th className="num">Paid</th><th className="num">Balance</th><th>Status</th><th /></tr>
        </thead>
        <tbody>
          {data.invoices.length === 0 && <tr><td colSpan={9} className="billing-empty">Nothing owed.</td></tr>}
          {data.invoices.map((r) => (
            <React.Fragment key={r.id}>
            <tr className={r.days_overdue > 0 ? 'billing-row-overdue' : ''}>
              <td>{r.number}</td>
              <td>{r.student_name}</td>
              <td>{fmtDate(r.issue_date)}</td>
              <td title={r.due_date_assumed ? 'No due date on the invoice — standard 14-day terms assumed' : ''}>
                {fmtDate(r.due_date)}{r.due_date_assumed ? '*' : ''}
                {r.deadline_moved && <span className="billing-sub">new deadline</span>}
              </td>
              <td className="num">{r.currency} {Number(r.amount_due).toFixed(2)}</td>
              <td className="num">{Number(r.paid).toFixed(2)}</td>
              <td className="num">
                <strong>{Number(r.balance).toFixed(2)}</strong>
                {Number(r.balance) < Number(r.net_due) && <span className="billing-sub">left of {Number(r.net_due).toFixed(2)}</span>}
              </td>
              <td>
                {r.days_overdue > 0
                  ? <span className="billing-pill billing-pill-red">{r.days_overdue} day{r.days_overdue === 1 ? '' : 's'} overdue</span>
                  : <span className="billing-pill">Not yet due</span>}
              </td>
              <td className="billing-row-actions">
                <button onClick={() => docPdf(r)} title="Invoice PDF"><FiDownload size={13} /> Invoice</button>
                <button onClick={() => statement(r)} title="Client's statement of account"><FiDownload size={13} /> Statement</button>
                <ClientFileButton student={r.student} />
                {canRecordPayments() && (
                  <button onClick={() => setDeadlineFor(deadlineFor === r.id ? null : r.id)}
                    title="Give the client a new deadline for what's left">New deadline</button>
                )}
              </td>
            </tr>
            {deadlineFor === r.id && (
              <tr><td colSpan={9}>
                <NewDeadline invoiceId={r.id} onSaved={() => { setDeadlineFor(null); load(); }} onCancel={() => setDeadlineFor(null)} />
              </td></tr>
            )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <h4 style={{ marginTop: 24 }}>Quotations expiring soon</h4>
      <table className="billing-table">
        <thead>
          <tr><th>Quotation</th><th>Client</th><th>Issued</th><th>Valid until</th><th className="num">Amount</th><th>Status</th><th /></tr>
        </thead>
        <tbody>
          {data.quotations.length === 0 && <tr><td colSpan={7} className="billing-empty">No sent quotations expiring in the next 30 days.</td></tr>}
          {data.quotations.map((r) => (
            <tr key={r.id} className={r.days_left <= 0 ? 'billing-row-overdue' : ''}>
              <td>{r.number}</td>
              <td>{r.student_name}</td>
              <td>{fmtDate(r.issue_date)}</td>
              <td>{fmtDate(r.valid_until)}</td>
              <td className="num">{r.currency} {Number(r.amount_due).toFixed(2)}</td>
              <td>
                {r.days_left < 0 && <span className="billing-pill billing-pill-red">Expired {-r.days_left} day{r.days_left === -1 ? '' : 's'} ago</span>}
                {r.days_left === 0 && <span className="billing-pill billing-pill-red">Expires today</span>}
                {r.days_left > 0 && (
                  <span className={`billing-pill ${r.days_left <= 3 ? 'billing-pill-amber' : ''}`}>
                    {r.days_left} day{r.days_left === 1 ? '' : 's'} left
                  </span>
                )}
              </td>
              <td className="billing-row-actions">
                <button onClick={() => docPdf(r)} title="Quotation PDF"><FiDownload size={13} /> Quotation</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="billing-hint" style={{ marginTop: 12 }}>
        * No due date was set on that invoice, so 14 days after issue is assumed. New invoices get a 14-day due date
        automatically when issued without one.
      </p>
    </div>
  );
}

export default function Billing() {
  const [tab, setTab] = useState('outstanding');
  const [fileStudent, setFileStudent] = useState(null);
  const openClientFile = useCallback((student) => { setFileStudent(student); setTab('client-file'); }, []);
  useOpenParam('student', openClientFile);
  return (
    <ClientFileContext.Provider value={openClientFile}>
    <div className="billing-page">
      <h1>Billing documents</h1>
      <div className="billing-tabs">
        <button className={tab === 'outstanding' ? 'active' : ''} onClick={() => setTab('outstanding')}>Outstanding</button>
        <button className={tab === 'client-file' ? 'active' : ''} onClick={() => setTab('client-file')}>Client billing file</button>
        <button className={tab === 'price-book' ? 'active' : ''} onClick={() => setTab('price-book')}>Price book</button>
        <button className={tab === 'template' ? 'active' : ''} onClick={() => setTab('template')}>Document template</button>
        <button className={tab === 'quotations' ? 'active' : ''} onClick={() => setTab('quotations')}>Quotations & invoices</button>
        <button className={tab === 'receipts' ? 'active' : ''} onClick={() => setTab('receipts')}>Receipts</button>
        <button className={tab === 'forecasts' ? 'active' : ''} onClick={() => setTab('forecasts')}>Budget forecasts</button>
        <button className={tab === 'agreements' ? 'active' : ''} onClick={() => setTab('agreements')}>Service agreements</button>
      </div>
      {tab === 'outstanding' && <Outstanding />}
      {tab === 'client-file' && <ClientFile initialStudent={fileStudent} />}
      {tab === 'price-book' && <PriceBook />}
      {tab === 'template' && <TemplateEditor />}
      {tab === 'quotations' && <QuotationsInvoices />}
      {tab === 'receipts' && <Receipts />}
      {tab === 'forecasts' && <Forecasts />}
      {tab === 'agreements' && <Agreements />}
    </div>
    </ClientFileContext.Provider>
  );
}
